import { Helmet } from 'react-helmet';

const SEOHead = () => {
  const siteUrl = typeof window !== 'undefined' 
    ? window.location.origin 
    : (process.env.REACT_APP_SITE_URL || 'https://devanshgupta.com');

  const seoData = {
    title: "Devansh Gupta | Finance Student & Social Impact Leader - Bocconi University",
    description: "Devansh Gupta - Università Bocconi student specializing in International Economics & Management. Finance intern, business consultant, and mentor to 600+ students. Based in Milan, Italy.",
    keywords: "Devansh Gupta, Bocconi University, Finance Student, Economics, Business Student Milan, International Economics Management, Finance Intern, Social Impact Leader, Student Mentor, Kolkata, India, Housy, Magnum Finvest, Strategy Market Development",
    author: "Devansh Gupta",
    url: siteUrl,
    image: `${siteUrl}/images/profile.png`,
    type: "profile"
  };

  // Enhanced Person Schema with more details
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Devansh Gupta",
    "givenName": "Devansh",
    "familyName": "Gupta",
    "jobTitle": "Finance & Economics Student",
    "description": "Finance and economics student at Università Bocconi specializing in financial analysis, business strategy, and social impact leadership.",
    "url": siteUrl,
    "email": "guptadevansh559321@gmail.com",
    "image": `${siteUrl}/images/profile.png`,
    "sameAs": [
      "https://www.linkedin.com/in/devansh-gupta-81b306237/"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Milan",
      "addressCountry": "Italy"
    },
    "alumniOf": [
      {
        "@type": "EducationalOrganization",
        "name": "Lakshmipat Singhania Academy",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Kolkata",
          "addressCountry": "India"
        }
      }
    ],
    "affiliation": {
      "@type": "EducationalOrganization",
      "name": "Università Bocconi",
      "url": "https://www.unibocconi.eu/",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Milan",
        "addressCountry": "Italy"
      }
    },
    "knowsAbout": [
      "Finance",
      "Economics",
      "Business Strategy",
      "Financial Analysis",
      "International Economics",
      "Portfolio Management",
      "Market Research",
      "Financial Modeling",
      "Strategic Planning"
    ],
    "hasOccupation": [
      {
        "@type": "Occupation",
        "name": "Strategy & Market Development Intern",
        "occupationLocation": {
          "@type": "City",
          "name": "Milan"
        },
        "estimatedSalary": {
          "@type": "MonetaryAmount",
          "currency": "EUR"
        }
      }
    ],
    "award": [
      "Head of Recruitment at VOYAGE Bocconi",
      "Vice President Business Fest",
      "NSE Financial Markets Certification"
    ],
    "memberOf": [
      {
        "@type": "Organization",
        "name": "VOYAGE",
        "description": "Università Bocconi Student Organization"
      },
      {
        "@type": "Organization",
        "name": "HelpAge India",
        "description": "Fundraising Coordinator"
      }
    ]
  };

  // BreadcrumbList Schema for better navigation
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About",
        "item": `${siteUrl}/#about`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Experience",
        "item": `${siteUrl}/#experience`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Contact",
        "item": `${siteUrl}/#contact`
      }
    ]
  };

  // WebSite Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Devansh Gupta - Portfolio",
    "url": siteUrl,
    "description": "Professional portfolio of Devansh Gupta, Finance Student at Università Bocconi",
    "author": {
      "@type": "Person",
      "name": "Devansh Gupta"
    },
    "inLanguage": "en-US"
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang="en" />
      <title>{seoData.title}</title>
      <meta name="title" content={seoData.title} />
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content={seoData.keywords} />
      <meta name="author" content={seoData.author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={seoData.url} />
      
      {/* Geographic Tags */}
      <meta name="geo.region" content="IT-MI" />
      <meta name="geo.placename" content="Milan" />
      <meta name="geo.position" content="45.4642;9.1900" />
      <meta name="ICBM" content="45.4642, 9.1900" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={seoData.type} />
      <meta property="og:url" content={seoData.url} />
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:image" content={seoData.image} />
      <meta property="og:image:alt" content="Devansh Gupta - Finance Student at Bocconi University" />
      <meta property="og:site_name" content="Devansh Gupta Portfolio" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoData.url} />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={seoData.image} />
      <meta name="twitter:image:alt" content="Devansh Gupta - Finance Student" />

      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#0D1117" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Verification Tags (to be added when claimed) */}
      {/* <meta name="google-site-verification" content="YOUR_GOOGLE_VERIFICATION_CODE" /> */}
      
      {/* Structured Data - Person Schema */}
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>

      {/* Structured Data - Website Schema */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {/* Structured Data - Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
